import { forwardRef, useMemo } from 'react';
import { i18nInit } from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID } from '@elfsight/lib-utils';
import type { SubmitActionType } from '@elfsight/widget-form-builder/configurator';
import type { FormField } from '@elfsight/widget-form-builder/widget';
import {
  commonFieldsSettings,
  formSettings as defaultFormSettings,
  Form as FormContainer,
  FormProvider,
  submitButtonSettings,
  WidgetProvider
} from '@elfsight/widget-form-builder/widget';
import { WidgetI18nProvider } from '@elfsight/widget-i18n';
import { useIsMobile } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../constants/meta';
import { DEFAULT_FORM_FIELD_SETTINGS } from '../../constants';
import {
  getFieldFontSize,
  getFieldLineHeight,
  getFieldPadding,
  getSubmitButtonFontSize,
  getSubmitButtonHorizontalPadding,
  getSubmitButtonLineHeight,
  getSubmitButtonVerticalPadding
} from '../../utils/form-element-utils';
import { FormLayout } from './form-layout';

export type FormProps = {
  onFormSubmit: (submitAction: SubmitActionType) => void;
};

export const Form = forwardRef<HTMLDivElement, FormProps>(function Form(
  { onFormSubmit },
  forwardingRef
) {
  const settings = useSettings();
  const {
    barSize,
    barSizeMobile,
    formEmailPlaceholder,
    formSubmitButtonText,
    buttonStyle,
    buttonRadius,
    buttonColor,
    widgetId
  } = settings;

  const isMobile = useIsMobile();

  const derivedSize = isMobile ? barSizeMobile : barSize;
  const fieldFontSize = getFieldFontSize(derivedSize, isMobile);
  const fieldLineHeight = getFieldLineHeight(derivedSize, isMobile);
  const fieldPadding = getFieldPadding(derivedSize, isMobile);
  const submitButtonFontSize = getSubmitButtonFontSize(derivedSize, isMobile);
  const submitButtonLineHeight = getSubmitButtonLineHeight(
    derivedSize,
    isMobile
  );
  const submitButtonHorizontalPadding = getSubmitButtonHorizontalPadding(
    derivedSize,
    isMobile
  );
  const submitButtonVerticalPadding = getSubmitButtonVerticalPadding(
    derivedSize,
    isMobile
  );

  const fields = useMemo(() => {
    return [
      {
        ...DEFAULT_FORM_FIELD_SETTINGS,
        placeholder: formEmailPlaceholder
      } as FormField
    ];
  }, [formEmailPlaceholder]);

  const formSettings = {
    ...defaultFormSettings,
    ...commonFieldsSettings,
    ...settings,
    fields,
    fieldSpacing: 12,
    fieldActiveColor: 'rgba(17, 17, 17, 0.3)',
    fieldBorderType: 'outline',
    fieldBorderColor: 'rgba(17, 17, 17, 0.1)',
    fieldBorderRadius: buttonRadius,
    fieldFillColor: 'rgb(255, 255, 255)',
    fieldFontStyle: {
      fontSize: fieldFontSize,
      fontWeight: 'normal',
      fontStyle: 'normal'
    },
    lineHeight: fieldLineHeight / fieldFontSize,
    padding: fieldPadding,
    submitButtonProps: {
      ...submitButtonSettings.submitButtonProps,
      size: 'custom',
      font: {
        fontSize: submitButtonFontSize,
        fontWeight: 'bold',
        fontStyle: 'normal'
      },
      text: formSubmitButtonText,
      mode: buttonStyle === 'filled' ? 'contain' : buttonStyle,
      color: buttonColor,
      borderRadius: buttonRadius,
      borderWidth: buttonStyle === 'filled' ? 0 : 1,
      lineHeight: submitButtonLineHeight / submitButtonFontSize,
      horizontalPadding: submitButtonHorizontalPadding,
      verticalPadding: submitButtonVerticalPadding,
      height: 2 * submitButtonVerticalPadding + submitButtonLineHeight
    },
    submitButtonStyle: {
      lineHeight: submitButtonLineHeight / submitButtonFontSize
    }
  };

  const i18n = useMemo(() => i18nInit(), []);

  return (
    <I18nProvider i18n={i18n}>
      <WidgetI18nProvider dictionary={{}} language="en" override={{}}>
        <WidgetProvider>
          <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
            <FormContainer
              {...formSettings}
              onSubmitSuccessful={onFormSubmit}
              submitAdditionalData={{
                type: meta.alias
              }}
            >
              <FormLayout ref={forwardingRef} formSettings={formSettings} />
            </FormContainer>
          </FormProvider>
        </WidgetProvider>
      </WidgetI18nProvider>
    </I18nProvider>
  );
});
